import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { SecondaryGhostLink } from '@bibliocommons/base-links';
import { routerShape } from '@bibliocommons/bc-prop-types';
import { QuestionMarkCircled } from '@bibliocommons/deprecated-base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import { SEARCH_TYPES } from 'app/constants/SearchConstants';
import EmbeddedChatWidget from 'app/components/shared/EmbeddedChatWidget';
import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import { CHAT_WIDGET_ID } from 'app/constants/LibraryConstants';
import getToggledFilter from 'app/helpers/facets/getToggledFilter';
import updateSearchQuery from 'app/helpers/search/updateSearchQuery';
import { SHELF_NAMES } from 'app/constants/ShelfConstants';
import { intlShape } from 'app/shapes';
import libraryShape from 'app/shapes/libraryShape';
import { activeFiltersShape, scopeShape } from 'app/shapes/search';
import AppliedFilters from '../AppliedFilters';
import ChatWidgetButton from '../ChatWidgetButton';
import DidNotFind from '../DidNotFind';
import DidYouMean from '../DidYouMean';
import ExternalSearchLink from '../ExternalSearchLink';

import './EmptySearchResult.scss';

const SUPPORTED_CHAT_WIDGET_IDS = [
  CHAT_WIDGET_ID.Q_WIDGET,
  CHAT_WIDGET_ID.LIB_ANSWERS,
  CHAT_WIDGET_ID.LIBRARY_H3LP,
  CHAT_WIDGET_ID.MOSIO,
  CHAT_WIDGET_ID.REF_CHATTER
];

const EmptySearchResult = ({
  activeFilters,
  alternativeScope,
  currentScope,
  didYouMeanQuerySuggestion,
  didYouMeanResultCount,
  intl,
  library,
  locked,
  query,
  renderHeading,
  renderMobileSearchBox,
  router,
  searchType,
  shelfName,
  staticEmptySearchHtml
}) => {
  const chatWidgets = library.get('chatWidgets', Immutable.Map());
  const didNotFindOptions = library.get('didNotFindOptions');
  const isSingleBranch = library.get('singleBranch');
  const searchTipsLink = library.get('searchTipsLink');
  const showExternalSearchLink = library.get('showExternalSearchLink', false);

  const hasAlternativeScope = Boolean(alternativeScope) && alternativeScope?.scopeId !== currentScope?.scopeId;
  let alternativeScopeUrl = '';
  if (hasAlternativeScope) {
    const alternativeScopeQuery = hasAlternativeScope
      ? new URLSearchParams({
          ...router.query,
          searchScope: alternativeScope.scopeId
        }).toString()
      : '';
    alternativeScopeUrl = `${router.location.pathname}?${alternativeScopeQuery}`;
  }

  let subtitle = '';
  if (shelfName) {
    subtitle = intl.formatMessage({ id: 'no_available_items' });
  } else if (query) {
    subtitle = intl.formatMessage(
      { id: 'Search.empty_search' },
      { scopeName: hasAlternativeScope ? currentScope.scopeName : null, query: <strong>{query}</strong> }
    );
  }

  const unapplyFilter = (fieldId, currentFilter) => {
    const updatedQuery = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      searchType,
      page: undefined,
      ...getToggledFilter({
        activeFilters,
        currentFilter,
        fieldId,
        separator: FILTER_SEPARATOR,
        override: false,
        prefix: FILTER_PREFIX
      })
    });

    router.push({ query: updatedQuery });
  };

  const unapplyAllFilters = () => {
    const updatedQuery = updateSearchQuery({
      currentQuery: router.query,
      page: undefined,
      searchType
    });

    router.push({ query: updatedQuery });
  };

  const toggleIsFiltersPinned = () => {
    const updatedQuery = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      locked: locked ? undefined : 'true',
      searchType
    });

    router.push({ query: updatedQuery });
  };

  const enabledChatWidget = chatWidgets.findLastKey((widget, widgetId) => {
    return widget.get('enabled') && SUPPORTED_CHAT_WIDGET_IDS.includes(widgetId);
  });

  const embeddedChatWidgets = SUPPORTED_CHAT_WIDGET_IDS.filter(chatWidgetId => {
    return chatWidgets.get(chatWidgetId) && chatWidgets.getIn([chatWidgetId, 'enabled']);
  });

  const hasActiveFilters = !activeFilters.isEmpty();
  const hasDidNotFindOptions = didNotFindOptions.get('isEnabled');

  return (
    <div className="cp-empty-search-result">
      {renderMobileSearchBox()}
      {renderHeading({ withDidYouMean: false })}

      <div className="empty-search-main-content">
        <div className="empty-search-sub-title">
          {subtitle && <h4>{subtitle}</h4>}
          {alternativeScopeUrl && (
            <div>
              <SecondaryGhostLink className="search-at-scope" dataKey="alternative-scope" href={alternativeScopeUrl}>
                <FormattedMessage id="Search.search_at_scope" values={{ scopeName: alternativeScope.scopeName }} />
              </SecondaryGhostLink>
            </div>
          )}
          <DidYouMean
            key="did-you-mean"
            querySuggestion={didYouMeanQuerySuggestion}
            resultCount={didYouMeanResultCount}
          />
          {hasActiveFilters && (
            <div className="active-filters">
              <FormattedMessage id="empty_search_clear_filters_info" />
              <div className="applied-filters">
                <AppliedFilters
                  appliedFilters={activeFilters}
                  hasPinnedToggle={false}
                  isPinned={locked}
                  isSingleBranch={isSingleBranch}
                  toggleIsPinned={toggleIsFiltersPinned}
                  unapplyAllFilters={unapplyAllFilters}
                  unapplyFilter={unapplyFilter}
                />
              </div>
            </div>
          )}
          {showExternalSearchLink && <ExternalSearchLink formatted query={query} searchType={searchType} />}
        </div>
        {hasDidNotFindOptions && (
          <div className="empty-search-help-tools">
            {searchTipsLink && (
              <a className="search-tips" href={searchTipsLink} target="_blank" rel="noopener noreferrer">
                <span aria-hidden>
                  <QuestionMarkCircled />
                  <FormattedMessage id="search_tips_link_text" />
                </span>
                <ScreenReaderMessage>
                  <FormattedMessage
                    id="ada_opens_a_new_tab"
                    values={{
                      linkText: <FormattedMessage id="search_tips_link_text" />
                    }}
                  />
                </ScreenReaderMessage>
              </a>
            )}
            {enabledChatWidget && (
              <span className="empty-search-chat-widget">
                <ChatWidgetButton widget={enabledChatWidget} />
              </span>
            )}
          </div>
        )}
      </div>

      {!hasDidNotFindOptions && (
        <div className="empty-search-secondary-content">
          {staticEmptySearchHtml && (
            <div className="static-empty-search-content">
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: staticEmptySearchHtml }}
              />
            </div>
          )}
          {embeddedChatWidgets.map(chatWidgetId => (
            <EmbeddedChatWidget key={chatWidgetId} widget={chatWidgetId} />
          ))}
        </div>
      )}

      {hasDidNotFindOptions && (
        <div className="empty-search-did-not-find">
          <DidNotFind didNotFindOptions={didNotFindOptions} query={query} />
        </div>
      )}
    </div>
  );
};

EmptySearchResult.defaultProps = {
  alternativeScope: null,
  didYouMeanResultCount: 0,
  didYouMeanQuerySuggestion: '',
  query: '',
  staticEmptySearchHtml: ''
};

EmptySearchResult.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  alternativeScope: scopeShape,
  currentScope: scopeShape.isRequired,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  intl: intlShape.isRequired,
  library: libraryShape.isRequired,
  locked: PropTypes.bool.isRequired,
  query: PropTypes.string,
  renderHeading: PropTypes.func.isRequired,
  renderMobileSearchBox: PropTypes.func.isRequired,
  router: routerShape.isRequired,
  searchType: PropTypes.oneOf(Object.values(SEARCH_TYPES)).isRequired,
  shelfName: PropTypes.oneOf(Object.values(SHELF_NAMES)),
  staticEmptySearchHtml: PropTypes.string
};

export default EmptySearchResult;
